import { Box, Typography } from '@mui/material';
import { EditButton, List, TopToolbar, useGetList, useListContext, useTranslate } from 'react-admin';

const PromptRow = ({ record }: { record: any }) => {
  // Query admin_analysis data for this prompt key using exact match
  const { data: adminAnalyses } = useGetList(
    'admin_analysis',
    {
      filter: { 'form_extract_key@_eq': record.key, 'deleted_at@_is_null': true },
      sort: { field: 'id', order: 'DESC' },
    },
    { enabled: !!record.key, retry: false },
  );

  // Extract version from key (e.g., "form_extract_prompt_2_1" -> "2.1")
  const getVersionFromKey = (key: string) => {
    if (key === 'form_extract_prompt') {
      return '1.0';
    }
    const match = key.match(/form_extract_prompt_(\d+)_(\d+)$/);
    if (match) {
      return `${match[1]}.${match[2]}`;
    }
    return key.replace('form_extract_prompt_', '');
  };

  // Get average score from score field in admin_analysis
  const getScoreData = () => {
    if (!adminAnalyses || adminAnalyses.length === 0) {
      return { score: 0, count: 0 }; // No data available
    }

    const scores = adminAnalyses
      .map((analysis: any) => {
        const scoreObj = analysis.score || {};
        return scoreObj[record.key] || 0;
      })
      .filter((score: number) => score > 0); // Only count records that have scores

    if (scores.length === 0) {
      return { score: 0, count: adminAnalyses.length };
    }

    const average = scores.reduce((sum: number, score: number) => sum + score, 0) / scores.length;
    return {
      score: Math.round(average),
      count: scores.length,
    };
  };

  const getScoreColor = (score: number) => {
    if (score >= 90) return '#1976d2'; // Blue
    if (score >= 80) return '#ed6c02'; // Orange
    return '#d32f2f'; // Red
  };

  const { score, count } = getScoreData();

  return (
    <Box sx={{ display: 'flex', border: '1px solid #e0e0e0', minHeight: '120px' }}>
      {/* Version Column */}
      <Box
        sx={{
          width: '150px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRight: '1px solid #e0e0e0',
          p: 2,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 'bold', textAlign: 'center' }}>
          {getVersionFromKey(record.key)}
        </Typography>
      </Box>

      {/* Prompt Column */}
      <Box sx={{ flex: 1, p: 2, borderRight: '1px solid #e0e0e0' }}>
        <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1, height: '100%', overflow: 'auto' }}>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-line', fontSize: '12px', lineHeight: 1.4 }}>
            {record.value}
          </Typography>
        </Box>
      </Box>

      {/* Score Column */}
      <Box
        sx={{
          width: '120px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          p: 2,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 'bold', color: getScoreColor(score) }}>
          {score}%
        </Typography>
        <Typography variant="caption" sx={{ color: '#666', fontSize: '10px' }}>
          ({count} tests)
        </Typography>
        <EditButton
          resource="setting"
          record={record}
          label=""
          variant="outlined"
          size="small"
          sx={{ minWidth: 'auto', p: 1 }}
        />
      </Box>
    </Box>
  );
};

const PromptListActions = () => <TopToolbar></TopToolbar>;

const PromptListView = () => {
  const { data } = useListContext();
  const translate = useTranslate();

  return (
    <Box>
      {/* Table Header */}
      <Box sx={{ display: 'flex', bgcolor: '#f0f0f0', border: '1px solid #e0e0e0', fontWeight: 'bold' }}>
        <Box sx={{ width: '150px', p: 2, borderRight: '1px solid #e0e0e0', textAlign: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            バージョン
          </Typography>
        </Box>
        <Box sx={{ flex: 1, p: 2, borderRight: '1px solid #e0e0e0', textAlign: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            プロンプト
          </Typography>
        </Box>
        <Box sx={{ width: '120px', p: 2, textAlign: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            スコア
          </Typography>
        </Box>
      </Box>

      {/* Table Rows */}
      {data?.map((record: any) => (
        <PromptRow key={record.id} record={record} />
      ))}
    </Box>
  );
};

export const PromptList = () => {
  const translate = useTranslate();

  return (
    <List
      title={translate('prompts.list.title')}
      resource="setting"
      actions={<PromptListActions />}
      perPage={25}
      sort={{ field: 'key', order: 'ASC' }}
      pagination={false}
      filterDefaultValues={{ key: 'form_extract_prompt' }}
    >
      <PromptListView />
    </List>
  );
};

export default PromptList;
